/* Préférences de notification — nine-answers item 7 (08/10) on Technical's contract answer: the five
   notifications × e-mail × immédiat / quotidien / off, stored per user through ONE path
   (GET / PUT /v1/app/messages/preferences). N1 « Quota en pause » is locked: its reason shows, the row never
   hides — the promise this screen always made. « Établissements » stays « Tous » in V1 (single location,
   ruling item 6). The digest's hour and zone come from the API, never from this file. No line names a review:
   the quota line counts participations, the retour line says a private message.
   Migration pack messages (v3.3 WF-030): « Mes notifications » — the hub's tab (`embarque`) and still its own page on
   /messages/preferences; the identity line « Prénom · Rôle »; « Enregistrer mes préférences ». The matrix is unchanged. */
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button, ErrorState, Note, Select, useToast } from '@/ui'
import { ApiError, saveNotificationPreferences, useNotificationPreferences, type NotificationCadence, type NotificationKind } from '@/lib/api'
import { currentUser, type Role } from '@/lib/scope'
import { Chargement, Obligatoire, Retour } from './parts'

/** The five, in the order the merchant meets them; `what` is the one sentence under the name. */
export const NOTIFICATION_LABELS: Record<NotificationKind, { label: string; what: string }> = {
  quota_paused: { label: 'Quota en pause', what: 'Le quota de participations du mois est atteint : vos campagnes sont en pause jusqu’au 1ᵉʳ, ou jusqu’à une offre supérieure.' },
  slice_capped: { label: 'Cadeau au plafond', what: 'Un cadeau a atteint son plafond du mois ; la roue continue avec les autres.' },
  feedback_received: { label: 'Nouveau retour privé', what: 'Un client vous a laissé un message privé à lire.' },
  campaign_ended: { label: 'Campagne terminée', what: 'Une campagne a atteint sa date de fin ; ses cadeaux gagnés restent valables.' },
  prize_cancelled: { label: 'Cadeau annulé', what: 'Un membre de l’équipe a annulé un cadeau émis.' },
}
export const CADENCE_LABELS: Record<NotificationCadence, string> = { immediate: 'Immédiat', daily: 'Quotidien', off: 'Off' }
/** The reason a locked row shows — the API's own 422 message, word for word. */
export const LOCKED_REASON = 'Vous devez savoir quand vos campagnes s’arrêtent'
const CADENCES: NotificationCadence[] = ['immediate', 'daily', 'off']
const zoneLabel = (tz: string) => tz === 'Europe/Paris' ? 'heure de Paris' : tz
/** The role, as the identity line says it (WF-030 « Léa · Propriétaire »). */
const ROLE_LABELS: Record<Role, string> = { owner: 'Propriétaire', admin: 'Administrateur', manager: 'Manager', staff: 'Collaborateur' }

/** `embarque`: rendered inside the Messages hub (its header carries the title) — standalone on /messages/preferences otherwise. */
export default function Preferences({ embarque = false }: { embarque?: boolean }) {
  const { data, isPending, isError, refetch } = useNotificationPreferences()
  const qc = useQueryClient()
  const toast = useToast()
  const [draft, setDraft] = useState<Partial<Record<NotificationKind, NotificationCadence>>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => { if (data) setDraft(Object.fromEntries(data.rows.map(r => [r.event, r.cadence]))) }, [data])

  if (isPending) return <Chargement />
  if (isError) return (
    <ErrorState what="Impossible de charger vos préférences."
      action={<Button variant="primary" size="sm" onClick={() => refetch()}>Réessayer</Button>} />
  )
  const cadenceOf = (event: NotificationKind) => draft[event] ?? data.rows.find(r => r.event === event)?.cadence ?? 'daily'
  const dirty = data.rows.some(r => !r.locked && cadenceOf(r.event) !== r.cadence)
  const hour = `${String(data.digestHour).padStart(2, '0')}:00`
  const user = currentUser()

  const save = async () => {
    setSaving(true); setError(null)
    try {
      const next = await saveNotificationPreferences(data.rows.map(r => ({ event: r.event, cadence: r.locked ? r.cadence : cadenceOf(r.event) })))
      qc.setQueryData(['notifications', 'preferences'], next)
      toast({ message: 'Préférences enregistrées.' })
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Impossible d’enregistrer. Réessayez.')
    } finally { setSaving(false) }
  }

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 820 }} data-testid="preferences">
      {!embarque && <Retour to="/messages?onglet=notifications" />}
      {!embarque && <h2 className="t-page" style={{ margin: 0 }}>Mes notifications</h2>}
      <p className="t-body" style={{ margin: 0 }} data-testid="preferences-identite"><b>{user.name} · {ROLE_LABELS[user.role]}</b> — vos préférences personnelles.</p>
      <p className="f-help" style={{ margin: 0 }}>
        Chaque notification part par e-mail aux propriétaires et administrateurs de l’espace. <b>Immédiat</b>&nbsp;: dès l’événement.
        <b> Quotidien</b>&nbsp;: un seul e-mail à {hour} ({zoneLabel(data.timezone)}) qui regroupe la journée. <b>Off</b>&nbsp;: rien par e-mail —
        la ligne reste visible dans l’application.
      </p>

      <div className="tablewrap">
        <table className="tbl" data-testid="preferences-table">
          <thead>
            <tr><th>Notification</th><th>Établissements</th><th>Canal</th><th>Rythme</th></tr>
          </thead>
          <tbody>
            {data.rows.map(r => {
              const { label, what } = NOTIFICATION_LABELS[r.event]
              return (
                <tr key={r.event} data-event={r.event} data-locked={r.locked || undefined}>
                  <td><b>{label}</b><br /><span className="f-help">{what}</span></td>
                  <td>Tous</td>
                  <td>E-mail</td>
                  <td>
                    {r.locked ? (
                      <span style={{ display: 'grid', gap: 4 }}>
                        <span>{CADENCE_LABELS[r.cadence]} <Obligatoire /></span>
                        <span className="f-help" data-locked-reason>{LOCKED_REASON}</span>
                      </span>
                    ) : (
                      <Select aria-label={`Rythme — ${label}`} value={cadenceOf(r.event)} onChange={e => setDraft(d => ({ ...d, [r.event]: e.target.value as NotificationCadence }))}>
                        {CADENCES.map(c => <option key={c} value={c}>{CADENCE_LABELS[c]}</option>)}
                      </Select>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {error && <Note tone="crit"><b>Non enregistré.</b> {error}</Note>}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="primary" size="sm" disabled={!dirty || saving} onClick={save}>{saving ? 'Enregistrement…' : 'Enregistrer mes préférences'}</Button>
        {dirty && !saving && <Button variant="quiet" size="sm" onClick={() => setDraft(Object.fromEntries(data.rows.map(r => [r.event, r.cadence])))}>Annuler</Button>}
        <span className="f-help">Vos choix ne valent que pour vous ; ces préférences ne changent pas celles de l’équipe.</span>
      </div>
    </div>
  )
}
